import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { Employee } from '../../types';
import { supabase, supabaseAuth } from '../lib/supabase';
import { logSupabaseQueryError, setErrorLoggingContext } from '../utils/errorLogging';

const DEFAULT_COMPANY_ID = '00000000-0000-0000-0000-000000000001';
const SUPER_ADMIN_EMAIL = 'sanilpetkar99@gmail.com';
const USER_CACHE_KEY = 'universal_app_user';
const ACTIVE_COMPANY_CACHE_KEY = 'opentask_active_company_id';
const LEGACY_ACTIVE_COMPANY_CACHE_KEY = 'universal_app_active_company';
const EMPLOYEES_CACHE_KEY = 'universalAppEmployees';
const TASKS_CACHE_KEY = 'universalAppTasks';
const STAFF_MANAGER_LINKS_CACHE_KEY = 'universalAppStaffManagerLinks';
const AUTH_SESSION_RETRY_DELAYS_MS = [250, 750, 1500];

export type AvailableCompany = {
  employeeId: string;
  role: Employee['role'];
  companyId: string;
  companyName: string;
  employeeRecord: Employee;
};

type AuthCompanyContextValue = {
  currentUser: Employee | null;
  activeEmployeeRecord: Employee | null;
  availableCompanies: AvailableCompany[];
  activeCompanyId: string | null;
  isSyncing: boolean;
  isCompanySwitching: boolean;
  authLoading: boolean;
  login: (user: Employee) => Promise<void>;
  logout: () => Promise<void>;
  switchCompany: (companyId: string) => void;
  finishCompanySwitch: () => void;
};

const AuthCompanyContext = createContext<AuthCompanyContextValue | null>(null);

const normalizeRole = (role: unknown, email?: unknown): Employee['role'] => {
  const normalizedEmail = String(email || '').trim().toLowerCase();
  if (normalizedEmail === SUPER_ADMIN_EMAIL) {
    return 'super_admin';
  }

  return role === 'owner' || role === 'manager' || role === 'staff' || role === 'super_admin' || role === 'accountant'
    ? role
    : 'staff';
};

const normalizeMobile = (mobile: unknown): string => {
  return String(mobile || '').replace(/\D/g, '');
};

const normalizeEmployeeProfile = (employee: Partial<Employee> & { id: string }): Employee => {
  const safeId = String(employee.id || `temp-${Date.now()}`);
  const safeEmail = String(employee.email || `${safeId}@taskpro.local`).trim();
  const safeName = String(employee.name || safeEmail.split('@')[0] || 'User').trim();
  const safeMobile = String(employee.mobile || '').trim();
  const rawManagerId = (employee as { manager_id?: string | null; managerId?: string | null }).manager_id
    ?? (employee as { manager_id?: string | null; managerId?: string | null }).managerId;
  const safeManagerId =
    typeof rawManagerId === 'string' && rawManagerId.trim()
      ? rawManagerId.trim()
      : null;

  return {
    id: safeId,
    name: safeName || 'User',
    email: safeEmail,
    mobile: safeMobile || safeId.slice(0, 10),
    role: normalizeRole(employee.role, safeEmail),
    company_id: String(employee.company_id || DEFAULT_COMPANY_ID),
    onesignal_id: employee.onesignal_id ? String(employee.onesignal_id) : null,
    auth_user_id: employee.auth_user_id ? String(employee.auth_user_id) : undefined,
    manager_id: safeManagerId,
  };
};

const toFallbackEmployeeFromAuthUser = (authUser: {
  id?: string;
  email?: string;
  user_metadata?: Record<string, unknown>;
  phone?: string;
}): Employee => {
  const metadata = authUser?.user_metadata || {};
  const email = String(authUser?.email || metadata.email || '').trim();
  const name = String(metadata.name || email.split('@')[0] || 'User').trim();
  const mobile = String(metadata.mobile || '').trim();

  return {
    id: String(authUser?.id || `temp-${Date.now()}`),
    name: name || 'User',
    email: email || `${authUser?.id || 'user'}@taskpro.local`,
    mobile: mobile || String(authUser?.id || '0000000000').slice(0, 10),
    role: 'staff',
    company_id: String(metadata.company_id || DEFAULT_COMPANY_ID),
  };
};

const isMissingColumnError = (error: unknown): boolean => {
  const message = String((error as { message?: string })?.message || '').toLowerCase();
  return message.includes('column') && message.includes('does not exist');
};

const isEmployeesPolicyError = (error: unknown): boolean => {
  const message = String((error as { message?: string })?.message || '').toLowerCase();
  return message.includes('infinite recursion') && message.includes('employees');
};

const readPersistedActiveCompanyId = (mobile?: string | null): string | null => {
  if (typeof window === 'undefined') return null;

  const normalizedMobile = normalizeMobile(mobile);
  const lookupKeys = normalizedMobile
    ? [
      ACTIVE_COMPANY_CACHE_KEY,
      `${ACTIVE_COMPANY_CACHE_KEY}:${normalizedMobile}`,
      `${LEGACY_ACTIVE_COMPANY_CACHE_KEY}:${normalizedMobile}`,
      LEGACY_ACTIVE_COMPANY_CACHE_KEY,
    ]
    : [ACTIVE_COMPANY_CACHE_KEY, LEGACY_ACTIVE_COMPANY_CACHE_KEY];

  for (const key of lookupKeys) {
    try {
      const value = String(localStorage.getItem(key) || '').trim();
      if (value) return value;
    } catch {
      // Ignore local storage read failures and continue with defaults.
    }
  }

  return null;
};

const readStoredActiveCompanyId = (): string => {
  if (typeof window === 'undefined') return '';

  try {
    return String(localStorage.getItem(ACTIVE_COMPANY_CACHE_KEY) || '').trim();
  } catch {
    return '';
  }
};

const writeStoredActiveCompanyId = (companyId: string | null | undefined) => {
  if (typeof window === 'undefined') return;

  const normalizedCompanyId = String(companyId || '').trim();
  if (!normalizedCompanyId) return;

  try {
    localStorage.setItem(ACTIVE_COMPANY_CACHE_KEY, normalizedCompanyId);
  } catch {
    // Ignore local storage write failures so active company state still updates in memory.
  }
};

const readCachedUserProfile = (): Employee | null => {
  if (typeof window === 'undefined') return null;

  try {
    const saved = localStorage.getItem(USER_CACHE_KEY);
    if (!saved || saved === 'undefined') return null;
    return normalizeEmployeeProfile(JSON.parse(saved) as Partial<Employee> & { id: string });
  } catch {
    return null;
  }
};

const persistActiveCompanyId = (mobile: string | null | undefined, companyId: string | null | undefined) => {
  if (typeof window === 'undefined') return;

  const normalizedMobile = normalizeMobile(mobile);
  const normalizedCompanyId = String(companyId || '').trim();
  if (!normalizedCompanyId) return;

  try {
    writeStoredActiveCompanyId(normalizedCompanyId);
    if (normalizedMobile) {
      localStorage.setItem(`${ACTIVE_COMPANY_CACHE_KEY}:${normalizedMobile}`, normalizedCompanyId);
    }
  } catch {
    // Ignore local storage write failures so company switching remains functional.
  }
};

const clearCompanyScopedCaches = () => {
  if (typeof window === 'undefined') return;

  try {
    localStorage.removeItem(EMPLOYEES_CACHE_KEY);
    localStorage.removeItem(TASKS_CACHE_KEY);
    localStorage.removeItem(STAFF_MANAGER_LINKS_CACHE_KEY);
  } catch {
    // Ignore local storage clear failures and continue runtime switching.
  }
};

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const getSessionWithRetry = async () => {
  let lastResult = await supabaseAuth.getSession();
  if (lastResult.data.session?.user || lastResult.error) {
    return lastResult;
  }

  for (const delayMs of AUTH_SESSION_RETRY_DELAYS_MS) {
    await wait(delayMs);
    lastResult = await supabaseAuth.getSession();
    if (lastResult.data.session?.user || lastResult.error) {
      return lastResult;
    }
  }

  return lastResult;
};

const getFallbackCompanyName = (companyId: string): string => {
  const normalizedCompanyId = String(companyId || '').trim();
  if (!normalizedCompanyId) return 'Workspace';
  return `Workspace ${normalizedCompanyId.slice(0, 8)}`;
};

const loadAvailableCompaniesForEmployee = async (
  _employee: Employee,
  sessionUserId?: string,
): Promise<AvailableCompany[]> => {
  let resolvedSessionUserId = String(sessionUserId || '').trim();
  if (!resolvedSessionUserId) {
    const { data: { session } } = await getSessionWithRetry();
    resolvedSessionUserId = String(session?.user?.id || '').trim();
  }

  console.log('[Switcher] sessionUserId:', resolvedSessionUserId);
  if (!resolvedSessionUserId) {
    return [];
  }

  const { data: self, error: selfError } = await supabase
    .from('employees')
    .select('mobile')
    .eq('id', resolvedSessionUserId)
    .single();

  console.log('[Switcher] self mobile:', self, selfError);
  if (selfError) {
    console.warn('Workspace self mobile lookup failed:', selfError);
    logSupabaseQueryError(selfError);
    return [];
  }

  const selfMobile = String((self as { mobile?: string | null } | null)?.mobile || '').trim();
  if (!selfMobile) {
    return [];
  }

  const { data: companies, error: companiesError } = await supabase
    .from('employees')
    .select('id, name, email, mobile, role, company_id, onesignal_id, auth_user_id, manager_id, companies(id, name)')
    .eq('mobile', selfMobile);

  console.log('[Switcher] companies result:', companies, companiesError);
  if (companiesError) {
    console.warn('Workspace lookup by mobile failed:', companiesError);
    logSupabaseQueryError(companiesError);
    return [];
  }

  type EmployeeCompanyRow = Employee & {
    companies?: { id?: string | null; name?: string | null } | { id?: string | null; name?: string | null }[] | null;
  };

  const companyRows = Array.isArray(companies) ? (companies as EmployeeCompanyRow[]) : [];
  const normalizedRows = companyRows.map((row) => normalizeEmployeeProfile(row));
  if (normalizedRows.length === 0) {
    return [];
  }

  console.log('[Switcher] returning entries:', companyRows.length);

  return companyRows
    .map((rawRow, index) => {
      const row = normalizedRows[index];
      const joinedCompany = Array.isArray(rawRow.companies) ? rawRow.companies[0] : rawRow.companies;
      const companyId = String(row.company_id || '').trim() || DEFAULT_COMPANY_ID;

      return {
        employeeId: row.id,
        role: normalizeRole(row.role),
        companyId,
        companyName:
          String(joinedCompany?.name || '').trim()
          || getFallbackCompanyName(companyId),
        employeeRecord: row,
      };
    })
    .sort((left, right) => left.companyName.localeCompare(right.companyName));
};

const isCachedUserForSession = (cachedUser: Employee | null, authUser: {
  id?: string;
  email?: string;
}): cachedUser is Employee => {
  if (!cachedUser) return false;

  const cachedAuthUserId = String(cachedUser.auth_user_id || '').trim();
  const cachedId = String(cachedUser.id || '').trim();
  const cachedEmail = String(cachedUser.email || '').trim().toLowerCase();
  const authUserId = String(authUser?.id || '').trim();
  const authEmail = String(authUser?.email || '').trim().toLowerCase();

  return (
    (!!authUserId && (cachedAuthUserId === authUserId || cachedId === authUserId)) ||
    (!!authEmail && cachedEmail === authEmail)
  );
};

const syncEmployeeProfileToDatabase = async (employee: Employee, authUserId?: string): Promise<Employee | null> => {
  const normalizedProfile = normalizeEmployeeProfile(employee);
  const payloadWithAuthLink = {
    id: normalizedProfile.id,
    name: normalizedProfile.name,
    email: normalizedProfile.email,
    mobile: normalizedProfile.mobile,
    role: normalizedProfile.role,
    company_id: normalizedProfile.company_id,
    auth_user_id: authUserId || normalizedProfile.auth_user_id || null,
    updated_at: new Date().toISOString(),
  };

  let { data, error } = await supabase
    .from('employees')
    .upsert(payloadWithAuthLink, { onConflict: 'id' })
    .select('*')
    .maybeSingle();

  if (error && isMissingColumnError(error)) {
    const { data: retryData, error: retryError } = await supabase
      .from('employees')
      .upsert({
        id: normalizedProfile.id,
        name: normalizedProfile.name,
        email: normalizedProfile.email,
        mobile: normalizedProfile.mobile,
        role: normalizedProfile.role,
        company_id: normalizedProfile.company_id,
        updated_at: new Date().toISOString(),
      }, { onConflict: 'id' })
      .select('*')
      .maybeSingle();

    data = retryData;
    error = retryError;
  }

  if (error) {
    if (!isEmployeesPolicyError(error)) {
      console.warn('Employee profile auto-sync failed:', error);
      logSupabaseQueryError(error);
    }
    return null;
  }

  return data ? normalizeEmployeeProfile(data as Employee) : null;
};

const resolveEmployeeProfileFromAuthUser = async (authUser: {
  id?: string;
  email?: string;
  user_metadata?: Record<string, unknown>;
  phone?: string;
}, preferredCompanyId?: string | null): Promise<Employee | null> => {
  const authUserId = String(authUser?.id || '').trim();
  const authCompanyId = String(preferredCompanyId || authUser?.user_metadata?.company_id || '').trim();

  if (authUserId) {
    const { data: byId, error: byIdError } = await supabase
      .from('employees')
      .select('*')
      .eq('id', authUserId)
      .limit(1)
      .maybeSingle();

    if (byId) {
      const normalizedById = normalizeEmployeeProfile(byId as Employee);
      if (!authCompanyId || normalizedById.company_id === authCompanyId) {
        return normalizedById;
      }
      // Company mismatch - still return this record; hydrateAvailableCompanies will pick the right workspace
      return normalizedById;
    }

    if (byIdError && !isEmployeesPolicyError(byIdError)) {
      console.warn('Session profile lookup by id failed:', byIdError);
      logSupabaseQueryError(byIdError);
    }

    let byAuthUserIdQuery = supabase
      .from('employees')
      .select('*')
      .eq('auth_user_id', authUserId)
      .limit(1);

    if (authCompanyId) {
      byAuthUserIdQuery = byAuthUserIdQuery.eq('company_id', authCompanyId);
    }

    let { data: byAuthUserId, error: byAuthUserIdError } = await byAuthUserIdQuery.maybeSingle();

    if (!byAuthUserId && authCompanyId && !byAuthUserIdError) {
      const retry = await supabase
        .from('employees')
        .select('*')
        .eq('auth_user_id', authUserId)
        .limit(1)
        .maybeSingle();
      byAuthUserId = retry.data;
      byAuthUserIdError = retry.error;
    }

    if (byAuthUserId) {
      return byAuthUserId as Employee;
    }

    if (byAuthUserIdError && !isMissingColumnError(byAuthUserIdError) && !isEmployeesPolicyError(byAuthUserIdError)) {
      console.warn('Session profile lookup by auth_user_id failed:', byAuthUserIdError);
      logSupabaseQueryError(byAuthUserIdError);
    }
  }

  return null;
};

export const AuthCompanyProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<Employee | null>(() => {
    try {
      const saved = localStorage.getItem(USER_CACHE_KEY);
      if (!saved || saved === 'undefined') return null;

      const parsed = JSON.parse(saved) as Partial<Employee>;
      return {
        ...parsed,
        role: normalizeRole(parsed?.role, parsed?.email),
        email: parsed?.email || `${parsed?.id || 'user'}@taskpro.local`,
        company_id: parsed?.company_id || DEFAULT_COMPANY_ID,
      } as Employee;
    } catch (error) {
      console.warn('Failed to parse cached user profile, clearing local cache.', error);
      logSupabaseQueryError(error);
      localStorage.removeItem(USER_CACHE_KEY);
      return null;
    }
  });
  const [availableCompanies, setAvailableCompanies] = useState<AvailableCompany[]>([]);
  const [activeCompanyId, setActiveCompanyId] = useState<string | null>(() =>
    readPersistedActiveCompanyId(
      (() => {
        try {
          const saved = localStorage.getItem(USER_CACHE_KEY);
          if (!saved || saved === 'undefined') return null;
          const parsed = JSON.parse(saved) as Partial<Employee>;
          return parsed?.mobile || null;
        } catch {
          return null;
        }
      })(),
    ),
  );
  const [isSyncing, setIsSyncing] = useState(false);
  const [isCompanySwitching, setIsCompanySwitching] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);

  const activeEmployeeRecord = currentUser;

  useEffect(() => {
    setErrorLoggingContext({
      userId: String(currentUser?.id || '').trim() || null,
      companyId: String(activeCompanyId || currentUser?.company_id || '').trim() || null,
    });
  }, [activeCompanyId, currentUser?.company_id, currentUser?.id]);

  useEffect(() => {
    if (readStoredActiveCompanyId()) {
      return;
    }

    writeStoredActiveCompanyId(activeCompanyId || currentUser?.company_id);
  }, [activeCompanyId, currentUser?.company_id]);

  const applyActiveWorkspace = useCallback((
    nextEmployee: Employee,
    nextCompanies: AvailableCompany[],
    nextCompanyId?: string | null,
  ) => {
    const storedId = readStoredActiveCompanyId();
    const validStoredId = storedId && nextCompanies.some((company) => company.companyId === storedId) ? storedId : null;
    const resolvedCompanyId = validStoredId || String(nextCompanyId || nextEmployee.company_id || '').trim();
    const normalizedEmployee = normalizeEmployeeProfile({
      ...nextEmployee,
      company_id: resolvedCompanyId || DEFAULT_COMPANY_ID,
    });

    setCurrentUser(normalizedEmployee);
    setAvailableCompanies(nextCompanies);
    setActiveCompanyId(normalizedEmployee.company_id);
    localStorage.setItem(USER_CACHE_KEY, JSON.stringify(normalizedEmployee));
    if (validStoredId === null) {
      persistActiveCompanyId(normalizedEmployee.mobile, normalizedEmployee.company_id);
    }
    return normalizedEmployee;
  }, []);

  const hydrateAvailableCompanies = useCallback(async (
    baseEmployee: Employee,
    preferredCompanyId?: string | null,
    sessionUserId?: string,
  ): Promise<Employee> => {
    const normalizedBaseEmployee = normalizeEmployeeProfile(baseEmployee);
    const memberships = await loadAvailableCompaniesForEmployee(normalizedBaseEmployee, sessionUserId);
    const persistedCompanyId = readStoredActiveCompanyId() || String(preferredCompanyId || '').trim();

    const fallbackEntry: AvailableCompany = {
      employeeId: normalizedBaseEmployee.id,
      role: normalizedBaseEmployee.role,
      companyId: String(normalizedBaseEmployee.company_id || DEFAULT_COMPANY_ID).trim() || DEFAULT_COMPANY_ID,
      companyName: getFallbackCompanyName(normalizedBaseEmployee.company_id || DEFAULT_COMPANY_ID),
      employeeRecord: normalizedBaseEmployee,
    };

    const nextCompanies = memberships.some((entry) => entry.companyId === fallbackEntry.companyId)
      ? memberships
      : [...memberships, fallbackEntry];
    const matchedEntry =
      nextCompanies.find((entry) => entry.companyId === persistedCompanyId)
      || nextCompanies[0];

    return applyActiveWorkspace(matchedEntry.employeeRecord, nextCompanies, matchedEntry.companyId);
  }, [applyActiveWorkspace]);

  const login = useCallback(async (user: Employee) => {
    const normalizedInputUser = normalizeEmployeeProfile(user);

    try {
      const rawCachedUser = localStorage.getItem(USER_CACHE_KEY);
      if (rawCachedUser) {
        const previousUser = JSON.parse(rawCachedUser) as Partial<Employee>;
        if (previousUser?.company_id && normalizedInputUser.company_id && previousUser.company_id !== normalizedInputUser.company_id) {
          clearCompanyScopedCaches();
        }
      }
    } catch {
      // Ignore cache parse issues and continue login flow.
    }

    setIsSyncing(true);
    setCurrentUser(normalizedInputUser);
    setActiveCompanyId(String(normalizedInputUser.company_id || DEFAULT_COMPANY_ID).trim() || DEFAULT_COMPANY_ID);
    localStorage.setItem(USER_CACHE_KEY, JSON.stringify(normalizedInputUser));
    persistActiveCompanyId(normalizedInputUser.mobile, normalizedInputUser.company_id);

    try {
      const { data: { session } } = await getSessionWithRetry();
      if (!session?.user) {
        await hydrateAvailableCompanies(normalizedInputUser, normalizedInputUser.company_id);
        return;
      }

      const employeeProfile = await resolveEmployeeProfileFromAuthUser(
        session.user,
        normalizedInputUser.company_id || readStoredActiveCompanyId(),
      );

      if (employeeProfile) {
        const normalizedEmployeeData = normalizeEmployeeProfile(employeeProfile);
        await hydrateAvailableCompanies(normalizedEmployeeData, normalizedEmployeeData.company_id, session.user.id);
        return;
      }

      const syncedUser = await syncEmployeeProfileToDatabase({
        ...normalizedInputUser,
        id: session.user.id,
      }, session.user.id);

      if (syncedUser) {
        await hydrateAvailableCompanies(syncedUser, syncedUser.company_id, session.user.id);
        return;
      }

      await hydrateAvailableCompanies(normalizedInputUser, normalizedInputUser.company_id, session.user.id);
    } catch (err) {
      console.warn('Login profile sync skipped due to session/profile fetch error:', err);
      logSupabaseQueryError(err);
      await hydrateAvailableCompanies(normalizedInputUser, normalizedInputUser.company_id);
    } finally {
      setIsSyncing(false);
      setAuthLoading(false);
    }
  }, [hydrateAvailableCompanies]);

  const logout = useCallback(async () => {
    try {
      await supabaseAuth.signOut();
    } catch (error) {
      console.warn('Auth sign-out failed, clearing local session anyway.', error);
      logSupabaseQueryError(error);
    } finally {
      setCurrentUser(null);
      setAvailableCompanies([]);
      setActiveCompanyId(null);
      setIsCompanySwitching(false);
      setIsSyncing(false);
      localStorage.removeItem(USER_CACHE_KEY);
      localStorage.removeItem(ACTIVE_COMPANY_CACHE_KEY);
      clearCompanyScopedCaches();
    }
  }, []);

  const switchCompany = useCallback((companyId: string) => {
    const nextCompanyId = String(companyId || '').trim();
    if (!nextCompanyId || nextCompanyId === String(activeCompanyId || '').trim()) {
      return;
    }

    const matchedWorkspace = availableCompanies.find((workspace) => workspace.companyId === nextCompanyId);
    if (!matchedWorkspace) {
      return;
    }

    clearCompanyScopedCaches();
    setIsCompanySwitching(true);
    setIsSyncing(true);

    const nextEmployee = normalizeEmployeeProfile({
      ...matchedWorkspace.employeeRecord,
      company_id: nextCompanyId,
    });

    setCurrentUser(nextEmployee);
    setActiveCompanyId(nextCompanyId);
    writeStoredActiveCompanyId(nextCompanyId);
    localStorage.setItem(USER_CACHE_KEY, JSON.stringify(nextEmployee));
    persistActiveCompanyId(nextEmployee.mobile, nextCompanyId);
  }, [activeCompanyId, availableCompanies]);

  const finishCompanySwitch = useCallback(() => {
    setIsCompanySwitching(false);
    setIsSyncing(false);
  }, []);

  useEffect(() => {
    const checkAuthSession = async () => {
      try {
        const { data: { session }, error } = await getSessionWithRetry();

        if (error) {
          console.error('Error fetching auth session:', error);
          logSupabaseQueryError(error);
          return;
        }

        if (!session?.user) {
          const cachedUser = readCachedUserProfile();
          if (cachedUser) {
            console.warn('Auth session temporarily unavailable; keeping cached user signed in.');
            await hydrateAvailableCompanies(cachedUser, readStoredActiveCompanyId() || cachedUser.company_id);
            return;
          }

          setCurrentUser(null);
          setAvailableCompanies([]);
          setActiveCompanyId(null);
          localStorage.removeItem(USER_CACHE_KEY);
          localStorage.removeItem(ACTIVE_COMPANY_CACHE_KEY);
          clearCompanyScopedCaches();
          return;
        }

        const persistedCompanyId = readStoredActiveCompanyId();
        const cachedUser = readCachedUserProfile();
        if (
          persistedCompanyId &&
          isCachedUserForSession(cachedUser, session.user) &&
          String(cachedUser.company_id || '').trim() === persistedCompanyId
        ) {
          await hydrateAvailableCompanies(cachedUser, persistedCompanyId, session.user.id);
          return;
        }

        const resolvedProfile = await resolveEmployeeProfileFromAuthUser(session.user, persistedCompanyId);

        if (resolvedProfile) {
          await hydrateAvailableCompanies(resolvedProfile, persistedCompanyId, session.user.id);
          return;
        }

        const fallbackUser = toFallbackEmployeeFromAuthUser(session.user);
        const syncedFallbackUser = await syncEmployeeProfileToDatabase(fallbackUser, session.user.id);
        const nextUser = syncedFallbackUser || fallbackUser;
        await hydrateAvailableCompanies(nextUser, persistedCompanyId, session.user.id);
      } catch (err) {
        console.error('Auth session check error:', err);
        logSupabaseQueryError(err);
      } finally {
        setAuthLoading(false);
      }
    };
    void checkAuthSession();
  }, [hydrateAvailableCompanies]);

  useEffect(() => {
    if (typeof supabaseAuth.onAuthStateChange !== 'function') {
      return;
    }

    const { data } = supabaseAuth.onAuthStateChange((event) => {
      if (event !== 'SIGNED_OUT') {
        return;
      }

      setCurrentUser(null);
      setAvailableCompanies([]);
      setActiveCompanyId(null);
      setIsCompanySwitching(false);
      setIsSyncing(false);
      localStorage.removeItem(USER_CACHE_KEY);
      localStorage.removeItem(ACTIVE_COMPANY_CACHE_KEY);
      clearCompanyScopedCaches();
    });

    return () => {
      data.subscription.unsubscribe();
    };
  }, []);

  const value = useMemo<AuthCompanyContextValue>(() => ({
    currentUser,
    activeEmployeeRecord,
    availableCompanies,
    activeCompanyId,
    isSyncing,
    isCompanySwitching,
    authLoading,
    login,
    logout,
    switchCompany,
    finishCompanySwitch,
  }), [
    currentUser,
    activeEmployeeRecord,
    availableCompanies,
    activeCompanyId,
    isSyncing,
    isCompanySwitching,
    authLoading,
    login,
    logout,
    switchCompany,
    finishCompanySwitch,
  ]);

  return (
    <AuthCompanyContext.Provider value={value}>
      {children}
    </AuthCompanyContext.Provider>
  );
};

export const useAuthCompany = (): AuthCompanyContextValue => {
  const context = useContext(AuthCompanyContext);
  if (!context) {
    throw new Error('useAuthCompany must be used within AuthCompanyProvider');
  }
  return context;
};
